import React, { useCallback, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

import ThreadDetails from '@ts-core/components/Forum/ThreadDetails';
import { ThreadAnswersList } from '@ts-core/components/Forum/ThreadAnswer';
import PostEditor from '@ts-core/components/Forum/PostEditor';

import BackButton from '@ts-core/components/BackButton';

import { useThreads, useThread, useThreadAnswers } from '@ts-core/hooks/forum';

const ThreadDetailsPage = () => {
  const { courseId, threadId } = useParams();
  const history = useHistory();

  const onThreadDeleted = useCallback(() => {
    history.push(`/${courseId}/forum`);
  }, [history, courseId]);

  const {
    thread,
    loading: loadingThread,
    refetchThread,
    onIncreaseThreadViews,
  } = useThread({
    courseId,
    threadId,
  });

  useEffect(() => {
    onIncreaseThreadViews();
  }, [onIncreaseThreadViews]);

  const {
    onDeleteThread,

    onUpvoteThread,
    onDownvoteThread,

    onReportThread,
  } = useThreads({ courseId, skipFetch: true, onThreadDeleted });

  const onThreadAnswerCreated = useCallback(() => {
    refetchThread();
  }, [refetchThread]);

  const {
    threadAnswers,
    loading: loadingThreadAnswers,

    onCreateThreadAnswer,
    creatingThreadAnswer,
    createThreadAnswerError,

    onUpvoteThreadAnswer,
    onDownvoteThreadAnswer,

    onDeleteAnswer,

    onReportThreadAnswer,
  } = useThreadAnswers({
    courseId,
    threadId,
    refetchQueries: refetchThread,
    onThreadAnswerCreated,
  });

  const handleGoBack = useCallback(() => {
    history.push(`/${courseId}/forum`);
  }, [history, courseId]);

  const handleDeleteThread = useCallback(
    (mThread) => {
      handleGoBack();
      onDeleteThread(mThread);
    },
    [handleGoBack, onDeleteThread]
  );

  const handleEditThread = useCallback(
    (mThread) => {
      history.push(`/${courseId}/forum/${mThread._id}/edit`);
    },
    [history, courseId]
  );

  const handleEditThreadAnswer = useCallback(
    (answer) => {
      history.push(`/${courseId}/forum/${threadId}/answers/${answer._id}/edit`);
    },
    [history, courseId, threadId]
  );

  return (
    <>
      <BackButton
        title={
          <FormattedMessage
            id="forum.postDetails.title"
            defaultMessage="Post"
          />
        }
        subtitle={<FormattedMessage id="forum.title" defaultMessage="Foro" />}
        onGoBack={handleGoBack}
      />
      <Box
        display="flex"
        flexDirection="column"
        maxWidth={{ md: '100%', lg: 1000 }}
        margin="auto"
      >
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          bgcolor="white"
          p={2}
        >
          <ThreadDetails
            thread={thread}
            loading={loadingThread}
            onDelete={handleDeleteThread}
            onUpvote={onUpvoteThread}
            onDownvote={onDownvoteThread}
            onEdit={handleEditThread}
            onReport={onReportThread}
          />
          <Divider />
          <Box fontSize="h6.fontSize" fontWeight={500} my={2} ml={2}>
            {thread?.answersCount !== undefined && (
              <FormattedMessage
                id="forum.label.answersCount"
                values={{
                  answersCount: thread?.answersCount,
                }}
                defaultMessage="{answersCount, number} {  answersCount, plural, one {Respuesta} other {Respuestas}}"
              />
            )}
          </Box>
          <ThreadAnswersList
            answers={threadAnswers}
            loading={loadingThreadAnswers}
            onDeleteAnswer={onDeleteAnswer}
            onEditAnswer={handleEditThreadAnswer}
            onUpvoteAnswer={onUpvoteThreadAnswer}
            onDownvoteAnswer={onDownvoteThreadAnswer}
            onReportAnswer={onReportThreadAnswer}
          />
        </Box>
        <PostEditor
          onSubmit={onCreateThreadAnswer}
          submitting={creatingThreadAnswer}
          submitError={createThreadAnswerError}
        />
      </Box>
    </>
  );
};

export default ThreadDetailsPage;
